import { useEffect, useState } from 'react';
import { ContainerAppFeature, ServiceEnvironment, User } from '@back4app2/sdk';
import { ReactComponent as CircleExclamationSVG } from '../../assets/images/circle-exclaimation-icon.svg';
import { BACK4APP_DOT_COM_SITE_URL } from '../../settings';
import back4app2 from '../../back4app2';
import Button from '../Button';
import { DsButton } from '../ds';
import useContainerPurchase from '../../hooks/useContainerPurchase';
import CustomerErrorMessage from './CustomErrorMessage';

/**
 * The card a pending app shows, and the second place a customer can pay.
 *
 * "Pay Now" used to be a link to the pricing site, which runs the LEGACY
 * checkout: it reports to a webhook host hardcoded to production, so an app
 * paid that way stayed pending here forever, and it only knows about plans —
 * the extras the customer had chosen never reached the subscription. It now
 * runs the same purchase as the creation wizard, through `useContainerPurchase`.
 */
const PaymentPendingErrorMessage = ({
  appId,
  mainServiceEnvironment,
  user,
  onPaid,
}: {
  appId: string,
  mainServiceEnvironment: ServiceEnvironment,
  user?: User,
  onPaid?: () => void,
}) => {
  const { purchase, checkoutModal } = useContainerPurchase();
  const [features, setFeatures] = useState<ContainerAppFeature[]>([]);
  const [isPaying, setIsPaying] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const plan = mainServiceEnvironment.plan;
  const isFreePlan = plan.name.includes('Free');

  useEffect(() => {
    let canceled = false;

    (async () => {
      try {
        const rows = await back4app2.containerAppFeatures(appId);

        if (!canceled) {
          setFeatures(rows);
        }
      } catch (e) {
        // The plan alone is still payable; the extras just would not be listed.
        console.error('could not load the app extras', e);
      }
    })();

    return () => { canceled = true; };
  }, [appId, plan.id]);

  const onPayNow = async () => {
    if (!user) {
      return;
    }

    setIsPaying(true);
    setErrorMessage('');

    const outcome = await purchase({
      appId,
      planId: plan.id,
      // The CATALOG id and the pack count. `feature.id` is the app's own row,
      // which the price lookup does not know — sending it failed every payment
      // for an app waiting on an extra.
      extras: features.map((feature) => ({
        featureId: feature.featureId,
        quantity: feature.quantity,
      })),
      user,
      summary: {
        planName: plan.name,
        planPrice: plan.price,
        lines: features.map((feature) => ({
          id: feature.id,
          name: feature.name,
          detail: feature.quantity > 1 ? `× ${feature.quantity}` : undefined,
          price: feature.price * feature.quantity,
        })),
      },
    });

    setIsPaying(false);

    if (outcome.ok) {
      onPaid?.();

      return;
    }

    // Nothing is said about a checkout the customer closed on purpose.
    if (outcome.reason === 'FAILED') {
      setErrorMessage(outcome.message || "We couldn't complete the payment.");
    }
  };

  const total = plan.price + features.reduce((sum, feature) => sum + feature.price * feature.quantity, 0);

  return (
    <>
      {checkoutModal}
      <CustomerErrorMessage type='error' icon={<CircleExclamationSVG width="27px" height="26px" className='text-error-red flex-none' />} headerText="Pending Payment" description={
        <>
          <div className="text-sm font-bold">Please complete a payment to proceed with the deployment.</div>
          {!mainServiceEnvironment.isFreePlanElegible && isFreePlan ? (
            <>
              <div className="text-sm mb-6">Unfortunately, the app type you've created is not eligible for our Free Plan. To continue with this app, please upgrade to a paid plan.</div>
              <Button type='submit'><a role="button" target="_blank" rel='noreferrer noopener' href={`${BACK4APP_DOT_COM_SITE_URL}/pricing/container-as-a-service?appId=${appId}&type=containers`}>Upgrade Now</a></Button>
            </>
          ) : isFreePlan ? (
            <>
              <div className="text-sm mb-6">Sorry, your app cannot be deployed as you have exceeded the usage limits of our Free Plan. Please upgrade to a paid plan to continue.</div>
              <Button type='submit'><a role="button" target="_blank" rel='noreferrer noopener' href={`${BACK4APP_DOT_COM_SITE_URL}/pricing/container-as-a-service?appId=${appId}&type=containers`}>Upgrade Now</a></Button>
            </>
          ) : (
            <>
              {/* The amount is named on the button, before the checkout
                  that asks for the card. */}
              <div className="text-sm mb-6">
                This app is on the {plan.name} plan and has not been paid for yet. Paying starts its first deployment.
              </div>
              {errorMessage ? (
                <div className="text-sm mb-4 text-error-red">{errorMessage}</div>
              ) : null}
              <DsButton variant="primary" onClick={onPayNow} isDisabled={isPaying || !user}>
                {isPaying
                  ? 'Working…'
                  : `Pay $${total}.00 / month`}
              </DsButton>
            </>
          )}
        </>
      } />
    </>
  );
};

export default PaymentPendingErrorMessage;
